import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';

export interface AdminNotification {
  id: string;
  type: string;
  kind: string | null;
  title: string | null;
  body: string | null;
  data: any;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsSummary {
  unreadCount: number;
  supportWaitingAdminCount: number;
  latest: AdminNotification[];
}

export interface ApiResponse<T> {
  status: string;
  message?: string;
  data: T;
}

const getSummary = async (): Promise<ApiResponse<NotificationsSummary>> => {
  const response = await api.get('/admin/notifications/summary');
  return response.data;
};

const markNotificationRead = async (id: string): Promise<ApiResponse<unknown>> => {
  const response = await api.post(`/admin/notifications/${id}/read`);
  return response.data;
};

const markAllRead = async (): Promise<ApiResponse<unknown>> => {
  const response = await api.post('/admin/notifications/read-all');
  return response.data;
};

export const useAdminNotificationsSummary = () => {
  return useQuery<ApiResponse<NotificationsSummary>>({
    queryKey: ['admin', 'notifications', 'summary'],
    queryFn: getSummary,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useAdminNotificationsActions = () => {
  const queryClient = useQueryClient();

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'notifications', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'notifications', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'notifications'] });
    },
  });

  return {
    markRead: (id: string) => markReadMutation.mutateAsync(id),
    markAllRead: () => markAllReadMutation.mutateAsync(),
    isMarkingRead: markReadMutation.isPending,
    isMarkingAllRead: markAllReadMutation.isPending,
  };
};

